/**
 * Route table + the persistent root.
 *
 * `AppRoot` is the Router's `root`, so it is mounted ONCE and survives every
 * navigation — the shell, its scroll position and the session all persist while
 * only the routed page swaps.
 */
import { Show, Suspense, lazy, type JSX, type ParentProps } from "solid-js";
import { Route } from "@solidjs/router";

import { useSession } from "~/core/auth/session";
import { RequirePermission } from "~/core/rbac/guard";
import type { Permission } from "~/core/rbac/permissions";
import { useThemeEffect } from "~/core/theme";
import { Spinner, Toaster } from "~/ui";

const LoginPage = lazy(() => import("~/features/auth/login-page"));
const AppShell = lazy(() => import("~/layout/app-shell"));

const Landing = lazy(() => import("~/features/misc/landing"));
const NoAccessPage = lazy(() => import("~/features/misc/no-access-page"));
const NotFoundPage = lazy(() => import("~/features/misc/not-found-page"));
const DashboardPage = lazy(() => import("~/features/dashboard/dashboard-page"));
const TransactionsListPage = lazy(() => import("~/features/transactions/transactions-list-page"));
const ControlPanelPage = lazy(() => import("~/features/transactions/control-panel-page"));
const ChatPage = lazy(() => import("~/features/chat/chat-page"));
const CustomersPage = lazy(() => import("~/features/customers/customers-page"));
const VerificationsPage = lazy(() => import("~/features/verifications/verifications-page"));
const MarketsPage = lazy(() => import("~/features/markets/markets-page"));
const PromotionsPage = lazy(() => import("~/features/campaigns/promotions-page"));
const ChallengesPage = lazy(() => import("~/features/campaigns/challenges-page"));
const NotificationsPage = lazy(() => import("~/features/notifications/notifications-page"));
const ReportsPage = lazy(() => import("~/features/reports/reports-page"));
const TeamPage = lazy(() => import("~/features/team/team-page"));
const SettingsPage = lazy(() => import("~/features/settings/settings-page"));
const AuditPage = lazy(() => import("~/features/audit/audit-page"));
const TransactionDetailPage = lazy(() => import("~/features/transactions/transaction-detail-page"));
const PricingPage = lazy(() => import("~/features/pricing/pricing-page"));
const RateGroupPage = lazy(() => import("~/features/pricing/group-page"));
const ReferenceRatesPage = lazy(() => import("~/features/reference-rates/reference-rates-page"));
const MarketDetailPage = lazy(() => import("~/features/markets/market-detail-page"));
const CustomerDetailPage = lazy(() => import("~/features/customers/customer-detail-page"));
const ReferralsPage = lazy(() => import("~/features/referrals/referrals-page"));
const BankAccountsPage = lazy(() => import("~/features/bank-accounts/bank-accounts-page"));

/** Full-viewport centred spinner — cold boot only, before we know who you are. */
function BootScreen(): JSX.Element {
  return (
    <div class="flex h-full items-center justify-center bg-[var(--c9-surface-sunken)]">
      <Spinner size="large" label="Starting" />
    </div>
  );
}

/**
 * Decides between the login screen and the app. Renders nothing until the initial
 * cookie-refresh has settled, so a signed-in operator never sees a flash of the
 * login form on reload.
 */
export function AppRoot(props: ParentProps): JSX.Element {
  useThemeEffect();
  const session = useSession();

  return (
    <Show when={session.ready()} fallback={<BootScreen />}>
      <Show when={session.signedIn()} fallback={<Suspense fallback={<BootScreen />}><LoginPage /></Suspense>}>
        <Suspense fallback={<BootScreen />}>
          <AppShell>{props.children}</AppShell>
        </Suspense>
        <Toaster />
      </Show>
    </Show>
  );
}

/**
 * Every route is lazy, so a module's code only downloads when an operator with
 * permission actually opens it. Each protected route is wrapped in
 * `RequirePermission` so a typed URL cannot reach a page the sidebar hides.
 */
function guarded(perform: Permission, Component: () => JSX.Element) {
  return () => (
    <RequirePermission perform={perform}>
      <Component />
    </RequirePermission>
  );
}

/**
 * A FUNCTION, not a module-level JSX value: building the tree at module scope
 * creates components outside any reactive root ("computations created outside a
 * `createRoot` … will never be disposed"). Calling it inside `render()` puts them
 * where they belong.
 */
export const routes = () => (
  <>
    {/* `/` is a redirect: it resolves to whatever this admin may actually open. */}
    <Route path="/" component={Landing} />
    <Route path="/dashboard" component={guarded("dashboard:view", DashboardPage)} />
    <Route path="/transactions" component={guarded("transactions:view", TransactionsListPage)} />
    <Route path="/control-panel" component={guarded("transactions:view", ControlPanelPage)} />
    <Route path="/transactions/:id" component={guarded("transactions:view", TransactionDetailPage)} />
    <Route path="/bank-accounts" component={guarded("transactions:view", BankAccountsPage)} />
    <Route path="/chat" component={guarded("chat:view", ChatPage)} />
    <Route path="/customers" component={guarded("users:view", CustomersPage)} />
    <Route path="/customers/:id" component={guarded("users:view", CustomerDetailPage)} />
    <Route path="/verifications" component={guarded("verifications:view", VerificationsPage)} />
    <Route path="/markets" component={guarded("markets:view", MarketsPage)} />
    <Route path="/markets/:id" component={guarded("markets:view", MarketDetailPage)} />
    <Route path="/pricing" component={guarded("markets:view", PricingPage)} />
    <Route path="/pricing/groups/:id" component={guarded("markets:view", RateGroupPage)} />
    <Route path="/reference-rates" component={guarded("markets:view", ReferenceRatesPage)} />
    <Route path="/promotions" component={guarded("promotions:view", PromotionsPage)} />
    <Route path="/challenges" component={guarded("challenges:view", ChallengesPage)} />
    <Route path="/referrals" component={guarded("referrals:view", ReferralsPage)} />
    <Route path="/notifications" component={guarded("notifications:view", NotificationsPage)} />
    <Route path="/reports" component={guarded("reports:view", ReportsPage)} />
    <Route path="/audit" component={guarded("reports:view", AuditPage)} />
    <Route path="/team" component={guarded("staff:view", TeamPage)} />
    <Route path="/settings" component={guarded("config:view", SettingsPage)} />
    <Route path="/no-access" component={NoAccessPage} />
    <Route path="*" component={NotFoundPage} />
  </>
);
